import {
  faArrowDownToLine,
  faWeightScale,
  faWarning
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import type { WeightUnit } from "@kanpla/api-contract";

import { useT, T } from "@repo/transifex";
import { autoId } from "~/utils/auto-id";
import { priceFormatter } from "~/utils/price-formatter";
import { Button, Dialog } from "~/components";
import { useAppContext } from "~/providers/app";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import {
  convertWeight,
  formatScaleWeight,
  getPricingBasis,
  getScaleUnitLabel,
  isSameUnitSystem
} from "~/utils/weight-units";
import { getUnitSystemLabel } from "~/utils/get-unit-system-label";
import { basketActions } from "~/providers/store/basket";
import { useSendWebEvent } from "~/hooks";
import { calculateUnitPriceByUnitSystem } from "~/utils/calculateUnitPriceByUnitSystem.ts";
import { useBasketContext } from "~/providers/store/basket/context";

type ScaleErrorDialogProps = {
  description: ReactNode;
  onClose: () => void;
  title: ReactNode;
};

const ScaleErrorDialog = ({
  description,
  onClose,
  title
}: ScaleErrorDialogProps) => {
  const t = useT();

  return (
    <Dialog onOutsideClick={onClose} open size="lg">
      <div>
        <div className="flex w-full flex-col gap-6 p-10 text-center">
          <FontAwesomeIcon className="h-16 text-danger-main" icon={faWarning} />
          <div>
            <p className="text-5xl font-bold text-danger-main">{title}</p>
            <p className="font-bold text-text-secondary">{description}</p>
          </div>
        </div>
        <div className="flex flex-col bg-background-secondary p-8">
          <div className="flex gap-6">
            <Button
              className="w-full flex-1"
              onClick={onClose}
              size="xl"
              variant="danger"
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export const WeightPage = () => {
  const t = useT();
  const { productId } = useParams();
  const navigate = useNavigate();
  const sendWebEvent = useSendWebEvent();

  const {
    hardware: {
      scales: { scale, unit: pairedUnit, weight: scaleInfo }
    }
  } = useAppContext();

  const { locale, currency } = useCurrentTillLocalisation();

  const products = useBasketContext((state) => state.products);
  const product = products.find((p) => p.id === productId);

  const onClose = () => {
    navigate("..");
    sendWebEvent({ type: "SCALE_DISCONNECT_REQUEST" });
  };

  if (!product) return <Navigate replace to="/pos" />;

  if (!scale) {
    return (
      <ScaleErrorDialog
        description={
          <T _str="This product requires a scale to be connected to be ordered" />
        }
        onClose={onClose}
        title={<T _str="No scale connected" />}
      />
    );
  }

  // The paired unit is known before the first frame arrives and survives a
  // reading reset, where the reading's own unit falls back to grams and would
  // flash the mismatch dialog on every imperial weigh. A kg scale's readings
  // already arrive in grams, so this is a relabel, not a conversion.
  const scaleUnit: WeightUnit = pairedUnit === "kg" ? "g" : pairedUnit;
  const basis = getPricingBasis(product.unitSystem);

  if (basis && !isSameUnitSystem(scaleUnit, basis.weightUnit)) {
    return (
      <ScaleErrorDialog
        description={
          <T
            _str="This scale weighs in {scaleUnit}, but this product is priced {pricingLabel}. Check the scale setup."
            pricingLabel={getUnitSystemLabel(product.unitSystem)}
            scaleUnit={getScaleUnitLabel(scaleUnit)}
          />
        }
        onClose={onClose}
        title={<T _str="Wrong scale unit" />}
      />
    );
  }

  const currentWeight =
    scaleInfo.weight -
    convertWeight(
      product.containerWeight,
      basis?.weightUnit ?? scaleUnit,
      scaleUnit
    );

  const hasStableWeight = scaleInfo.status === "stable" && currentWeight > 0;

  const unitPrice = calculateUnitPriceByUnitSystem({
    unitPrice: product.unitPrice,
    weight: currentWeight,
    unitSystem: product.unitSystem,
    weightUnit: scaleUnit
  });

  return (
    <Dialog onOutsideClick={onClose} open size="lg">
      <div>
        <div className="flex w-full flex-col gap-6 p-10 text-center">
          <h3 className="text-3xl font-bold">{product.name}</h3>
          <FontAwesomeIcon
            className="h-16 text-primary-main"
            icon={hasStableWeight ? faWeightScale : faArrowDownToLine}
          />
          <p className="text-5xl font-bold text-primary-main">
            {hasStableWeight
              ? `${formatScaleWeight(
                  currentWeight,
                  scaleInfo.decimals
                )} ${getScaleUnitLabel(scaleUnit)}`
              : t("Place item on scale")}
          </p>
        </div>
        <div className="flex flex-col bg-background-secondary p-8">
          {hasStableWeight ? (
            <div className="mb-6 flex items-center">
              <p className="flex-1 text-lg">{t("Price")}</p>
              <p className="text-4xl font-semibold">
                {priceFormatter({
                  value: unitPrice,
                  currency,
                  locale
                })}
              </p>
            </div>
          ) : null}
          <div className="flex gap-6">
            <Button
              className="w-full flex-1"
              onClick={onClose}
              size="xl"
              variant="danger"
            >
              {t("Cancel")}
            </Button>
            {hasStableWeight ? (
              <Button
                className="flex-[2]"
                onClick={() => {
                  basketActions.addVariantWeight(
                    product.id,
                    [
                      {
                        name: "scale_weight",
                        choices: [
                          {
                            amount: 1,
                            weight: currentWeight,
                            id: autoId(),
                            name: product.unitSystem,
                            ...(scaleUnit !== "g" && {
                              weightUnit: scaleUnit,
                              weightDecimals: scaleInfo.decimals
                            })
                          }
                        ]
                      }
                    ],
                    product.unitSystem,
                    !product.discount?.isAllowanceOver
                  );
                  onClose();
                }}
                size="xl"
                variant="primary"
              >
                {t("Add to basket")}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
